/**
 * One-time upgrade: persist ML-DSA-65 identity keys for v1/v2 bundles.
 *
 * New conversation sends only hybrid-sign when a PQ secret is in memory.
 * Logout/login does not re-run initialize E2E, so pre-hybrid identities
 * would otherwise keep emitting 64-byte Ed25519 signatures.
 *
 * Unpublished PQ secrets are never returned for signing — hybrid verify
 * fails closed without the matching public key.
 *
 * @module services/pqSigningUpgrade
 */

import { toBase64, clearBytes } from '@adieuu/crypto';
import type { IdentityApi } from '@adieuu/shared';
import {
  decryptKeyBundle,
  encryptKeyBundle,
  withPqSigningKeys,
  type DecryptedBundle,
} from './e2eKeyService';

export interface EnsurePublishedPqSigningKeysInput {
  identityId: string;
  passphrase: string;
  decrypted: DecryptedBundle;
  identityApi: Pick<IdentityApi, 'getPublicKeys' | 'getKeyBundle' | 'upgradePqSigning'>;
}

function withoutPq(bundle: DecryptedBundle): DecryptedBundle {
  return {
    signingPrivateKey: bundle.signingPrivateKey,
    webDevice: bundle.webDevice,
  };
}

async function persistUpgrade(
  identityId: string,
  passphrase: string,
  bundle: DecryptedBundle,
  identityApi: EnsurePublishedPqSigningKeysInput['identityApi'],
): Promise<{ ok: boolean; conflict: boolean }> {
  if (!bundle.pqSigningPrivateKey || !bundle.pqSigningPublicKey) {
    return { ok: false, conflict: false };
  }
  try {
    const encrypted = await encryptKeyBundle(bundle, passphrase);
    const resp = await identityApi.upgradePqSigning(identityId, {
      pqSigningPublicKey: toBase64(bundle.pqSigningPublicKey),
      encryptedBundle: encrypted.encryptedBundle,
      salt: encrypted.salt,
      nonce: encrypted.nonce,
    });
    if (resp.success) return { ok: true, conflict: false };
    return { ok: false, conflict: resp.error?.code === 'CONFLICT' };
  } catch (err) {
    console.warn('[Identity] PQ signing upgrade failed:', err);
    return { ok: false, conflict: false };
  }
}

async function refetchUpgradedBundle(
  identityId: string,
  passphrase: string,
  identityApi: EnsurePublishedPqSigningKeysInput['identityApi'],
): Promise<DecryptedBundle | null> {
  try {
    const fetched = await identityApi.getKeyBundle(identityId);
    if (!fetched.success || !fetched.data) return null;
    const again = await decryptKeyBundle(fetched.data, passphrase);
    if (again.pqSigningPrivateKey && again.pqSigningPublicKey) {
      return again;
    }
  } catch (err) {
    console.warn('[Identity] PQ signing upgrade refetch failed:', err);
  }
  return null;
}

/**
 * Ensure the decrypted bundle has published ML-DSA-65 keys.
 *
 * Generates keys for v1/v2 bundles and uploads them. If another device
 * already published a different PQ key, adopts that device's bundle.
 * On persist failure, returns Ed25519-only material so this session
 * does not emit unverifiable hybrid signatures.
 */
export async function ensurePublishedPqSigningKeys(
  input: EnsurePublishedPqSigningKeysInput
): Promise<DecryptedBundle> {
  const { identityId, passphrase, identityApi } = input;
  const { bundle, generated } = withPqSigningKeys(input.decrypted);

  const discardGenerated = () => {
    if (!generated) return;
    if (bundle.pqSigningPrivateKey) clearBytes(bundle.pqSigningPrivateKey);
    if (bundle.pqSigningPublicKey) clearBytes(bundle.pqSigningPublicKey);
  };

  if (!generated) {
    try {
      const keys = await identityApi.getPublicKeys(identityId);
      if (keys.success && keys.data?.pqSigningPublicKey) {
        return bundle;
      }
    } catch (err) {
      console.warn('[Identity] could not read PQ public key before publish:', err);
    }
    const published = await persistUpgrade(identityId, passphrase, bundle, identityApi);
    if (published.ok) return bundle;
    const peer = await refetchUpgradedBundle(identityId, passphrase, identityApi);
    if (peer) return peer;
    return withoutPq(input.decrypted);
  }

  const published = await persistUpgrade(identityId, passphrase, bundle, identityApi);
  if (published.ok) return bundle;

  const peer = await refetchUpgradedBundle(identityId, passphrase, identityApi);
  if (peer) {
    discardGenerated();
    return peer;
  }

  discardGenerated();
  return withoutPq(input.decrypted);
}
